import * as React from "react";

import { useCommonArrowNavGroup } from "./useCommonArrowNavGroup";
import { useDisableTabTabster } from "./useDisableTabTabster";
import { useFirstFocusableRef } from "./useFirstFocusableHook";
import type { UseArrowNavigationGroupOptions } from "@fluentui/react-components";
import { useV9TooltipEnabled } from "@1js/acui-tooltip";

export type TabsterDivProps = {
  /**
   * Children elements within the div
   */
  children?: React.ReactNode;
  /**
   * Custom styles
   */
  className?: string;
  /**
   * Accessible name
   */
  ariaLabel?: string;
  /**
   * Callback ref to control focus on the tablist
   */
  firstFocusableRef?: React.Ref<{ focus: () => void }>;
  /**
   * If true, will add an arrow navigation group from tabster
   */
  useArrowNavGroup?: boolean;

  /**
   * Callback for the keydown event
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;

  /**
   * Option to focus the selected element instead of first
   */
  focusSelected?: boolean;

  /**
   * Option to let tabster control tab keydown events rather
   * than overwriting them
   */
  useTabsterTabControl?: boolean;

  /**
   * Option to indicate which axis should use tabster for arrow navigation
   */
  axis?: UseArrowNavigationGroupOptions["axis"];

  /**
   * Optional callback to be notified when focus moves to a new element
   */
  onActiveElementChanged?: ((element?: HTMLElement) => void) | undefined;

  /**
   * Option to remove tab keydown events from the div
   */
  moveFocusRemoved?: boolean;
};

export const DivWithTabsterAttributes = React.forwardRef(
  (props: TabsterDivProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      useArrowNavGroup,
      className,
      ariaLabel,
      children,
      firstFocusableRef,
      onKeyDown,
      focusSelected,
      useTabsterTabControl,
      axis,
      onActiveElementChanged,
      moveFocusRemoved,
    } = props;

    const divRef = React.useRef<HTMLDivElement>(null);
    const tooltipEnabled = useV9TooltipEnabled();
    // Hook to ensure we focus the correct tab when we
    // call focus on the tablist container
    useFirstFocusableRef({ firstFocusableRef, focusSelected }, divRef);

    // Hook to allow focus to move using arrow keys
    const attributes = useCommonArrowNavGroup(
      useTabsterTabControl ? false : true,
      axis
    );
    const navGroupProperties = useArrowNavGroup ? attributes : {};

    // Hook to disable tab so we can manually move tab focus
    const tabsterDisabler = useDisableTabTabster();
    const disable = useTabsterTabControl ? {} : tabsterDisabler;

    // We need code to allow focus to bubble up so that keytips
    // on the ribbon tabs will dismiss if the user uses arrow keys
    React.useEffect(() => {
      if (onActiveElementChanged) {
        const onMoveFocus = (e: HTMLElementEventMap["tabster:movefocus"]) => {
          e.preventDefault();
          e.detail.next?.focus();
          e.detail.next && onActiveElementChanged(e.detail.next);
        };

        const div = divRef.current;

        if (tooltipEnabled) {
          !moveFocusRemoved &&
            div?.addEventListener("tabster:movefocus", onMoveFocus);
        } else {
          div?.addEventListener("tabster:movefocus", onMoveFocus);
        }

        return div
          ? () => {
              div.removeEventListener("tabster:movefocus", onMoveFocus);
            }
          : undefined;
      }
      return undefined;
    }, [divRef, onActiveElementChanged, moveFocusRemoved, tooltipEnabled]);

    return (
      <div
        role={"toolbar"}
        data-is-focusable={"true"}
        ref={divRef ? divRef : ref}
        className={className}
        tabIndex={useTabsterTabControl ? -1 : 0}
        aria-label={ariaLabel}
        {...disable}
        {...navGroupProperties}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    );
  }
);
